import { AuthRoles } from '@app/modules/auth-modules/auth.model';

/**
 * Route guard `data.ROLES` values: real AuthRoles plus logical keys checked against `profiles` / storage.
 */
export type AccountUserTypes = AuthRoles | 'musician' | 'individualBookingCustomer';

/** POST/DELETE /account/roles — body path accepts this slug (not an AuthRoles value). */
export type AccountRolesEndpointRole = AuthRoles | 'individualBookingCustomer';

interface TokenInfoContacts {
  vkUsername?: string;
  contactPhone?: string;
  phoneVerified?: boolean;
  hasTelegram?: boolean;
  hasMax?: boolean;
  hasViber?: boolean;
  hasWhatsapp?: boolean;
}

/** GET /account/userInfo — `profiles[].profileKind` (string or numeric from API). */
export type AccountProfileKind =
  | 'Musician'
  | 'BookingPerformer'
  | 'BookingIndividualCustomer'
  | 'BookingVenue'
  | 'Partner'
  | 0
  | 1
  | 2
  | 3
  | 4;

/** GET /account/userInfo — `profiles[].status`. */
export type AccountProfileStatus =
  | 'Active'
  | 'Draft'
  | 'Published'
  | 'Inactive'
  | 0
  | 1
  | 2
  | 3;

/**
 * Account profile row from GET /account/userInfo `profiles`.
 */
export interface AccountProfileVm {
  profileKind: AccountProfileKind;
  displayName?: string | null;
  status: AccountProfileStatus;
  userId?: number | null;
  performerId?: string | null;
  bookingCustomerId?: string | null;
  partnerId?: number | null;
  legalAccountConfirmed?: boolean | null;
  readyToAcceptApplications?: boolean | null;
  partnerType?: string | null;
}

export function accountProfileKindIsMusician(
  kind: AccountProfileKind | string | number | undefined | null
): boolean {
  if (kind === undefined || kind === null) {
    return false;
  }
  return kind === 'Musician' || kind === 0;
}

export function accountProfileKindIsBookingIndividual(
  kind: AccountProfileKind | string | number | undefined | null
): boolean {
  if (kind === undefined || kind === null) {
    return false;
  }
  return kind === 'BookingIndividualCustomer' || kind === 2;
}

export function accountProfileKindIsBookingVenue(
  kind: AccountProfileKind | string | number | undefined | null
): boolean {
  if (kind === undefined || kind === null) {
    return false;
  }
  return kind === 'BookingVenue' || kind === 3;
}

export function accountProfileKindIsBookingPerformer(
  kind: AccountProfileKind | string | number | undefined | null
): boolean {
  if (kind === undefined || kind === null) {
    return false;
  }
  return kind === 'BookingPerformer' || kind === 1;
}

export function accountProfileStatusIsInactive(
  status: AccountProfileStatus | string | number | undefined | null
): boolean {
  if (status === undefined || status === null) {
    return false;
  }
  return status === 'Inactive' || status === 3;
}

/**
 * Account profile: GET /account/userInfo response.
 * (`name` is display name; PUT /account/userInfo sends it as `name`.)
 */
export interface AccountUserInfo {
  id?: number;
  name?: string;
  lastName?: string;
  email?: string;
  picPath?: string;
  cityId?: number;
  isBanned?: boolean;
  emailConfirmed?: boolean;
  birthday?: string;
  contacts?: TokenInfoContacts;
  roles?: AuthRoles[];
  profiles?: AccountProfileVm[] | null;
}

/** True if userInfo includes an active (non-inactive) Musician profile. */
export function hasMusicianAccountProfile(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): boolean {
  const list = user?.profiles;
  if (!list?.length) {
    return false;
  }
  return list.some(
    p =>
      accountProfileKindIsMusician(p.profileKind) && !accountProfileStatusIsInactive(p.status)
  );
}

/** Booking individual customer: `profiles` row (BookingIndividualCustomer / 2). */
export function hasBookingIndividualCustomerAccount(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): boolean {
  if (!user) {
    return false;
  }
  const list = user.profiles;
  if (!list?.length) {
    return false;
  }
  return list.some(
    p =>
      accountProfileKindIsBookingIndividual(p.profileKind) &&
      !accountProfileStatusIsInactive(p.status)
  );
}

/**
 * Any active booking-customer profile: individual or venue (BookingVenue).
 */
export function hasBookingCustomerAccount(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): boolean {
  if (!user) {
    return false;
  }
  const list = user.profiles;
  if (!list?.length) {
    return false;
  }
  return list.some(
    p =>
      (accountProfileKindIsBookingIndividual(p.profileKind) ||
        accountProfileKindIsBookingVenue(p.profileKind)) &&
      !accountProfileStatusIsInactive(p.status)
  );
}

/** Booking performer: `profiles` row (BookingPerformer / 1). */
export function hasBookingPerformerAccount(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): boolean {
  if (!user) {
    return false;
  }
  const list = user.profiles;
  if (!list?.length) {
    return false;
  }
  return list.some(
    p =>
      accountProfileKindIsBookingPerformer(p.profileKind) &&
      !accountProfileStatusIsInactive(p.status)
  );
}

/** Active venue booking customer row — `bookingCustomerId` for venue profile APIs. */
export function getActiveBookingVenueCustomerId(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): string | null {
  const list = user?.profiles;
  if (!list?.length) {
    return null;
  }
  const p = list.find(
    x =>
      accountProfileKindIsBookingVenue(x.profileKind) &&
      !accountProfileStatusIsInactive(x.status) &&
      x.bookingCustomerId != null &&
      String(x.bookingCustomerId).trim() !== ''
  );
  const id = p?.bookingCustomerId;
  return typeof id === 'string' && id.length ? id : null;
}

export function hasBookingVenueCustomerAccount(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): boolean {
  if (!user) {
    return false;
  }
  const list = user.profiles;
  if (!list?.length) {
    return false;
  }
  return list.some(
    p =>
      accountProfileKindIsBookingVenue(p.profileKind) && !accountProfileStatusIsInactive(p.status)
  );
}

/** Active individual booking customer row — `bookingCustomerId` for GET/PUT individuals/profile APIs. */
export function getActiveBookingIndividualCustomerId(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): string | null {
  const list = user?.profiles;
  if (!list?.length) {
    return null;
  }
  const p = list.find(
    x =>
      accountProfileKindIsBookingIndividual(x.profileKind) &&
      !accountProfileStatusIsInactive(x.status) &&
      x.bookingCustomerId != null &&
      String(x.bookingCustomerId).trim() !== ''
  );
  const id = p?.bookingCustomerId;
  return typeof id === 'string' && id.length ? id : null;
}

/**
 * Active bookingCustomerId for hire requests (individual or venue profile).
 */
export function getActiveBookingCustomerId(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): string | null {
  const list = user?.profiles;
  if (!list?.length) {
    return null;
  }
  const p = list.find(
    x =>
      (accountProfileKindIsBookingIndividual(x.profileKind) ||
        accountProfileKindIsBookingVenue(x.profileKind)) &&
      !accountProfileStatusIsInactive(x.status) &&
      x.bookingCustomerId != null &&
      String(x.bookingCustomerId).trim() !== ''
  );
  const id = p?.bookingCustomerId;
  return typeof id === 'string' && id.length ? id : null;
}

/**
 * Active booking performer id for incoming invitations.
 */
export function getActiveBookingPerformerId(
  user: Pick<AccountUserInfo, 'profiles'> | null | undefined
): string | null {
  const list = user?.profiles;
  if (!list?.length) {
    return null;
  }
  const p = list.find(
    x =>
      accountProfileKindIsBookingPerformer(x.profileKind) &&
      !accountProfileStatusIsInactive(x.status) &&
      x.performerId != null &&
      String(x.performerId).trim() !== ''
  );
  const id = p?.performerId;
  return typeof id === 'string' && id.length ? id : null;
}

/**
 * Export of UserSettings interface
 */
export interface AccountSettings {
  email: boolean;
  musicianSearch: boolean;
  news: boolean;
  telegramId: string;
  telegramNotifyEnabled: boolean;
  vkId: string;
  vkNotifyEnabled: boolean;
}

/**
 * Api: v1
 * Path: /account/forgotPassword
 * @Request
 */
export interface AccountForgotPasswordRequest {
  email: string;
}

export interface RecovertPassword {
  userId: string;
  password: string;
  confirmPassword: string;
  code: string;
}

export interface ChangePassword {
  oldPassword: string;
  newPassword: string;
}

/** PUT /account/userInfo/contacts request body. */
export interface AccountContactsUpdate {
  vkUsername?: string;
  contactPhone?: string;
  hasTelegram?: boolean;
  hasMax?: boolean;
}
